import { useState, useEffect } from 'react'
import { useToast } from '@chakra-ui/react'
import { doc, setDoc } from 'firebase/firestore'
import moment from 'moment'
import isRawDateValid from '../utils/isRawDateValid'
import randomId from '../utils/randomId'
import {
  AmountUnit,
  DurationUnit,
  FrequencyUnit,
  Method,
  Percentage,
  Regimen,
  SelectedFields,
  Usage,
} from '../types/Regimen'
import useActiveUid from './useActiveUid'
import { getEffectiveServices } from '../utils/firebaseServiceContext'

/**
 * Standalone helper — single source of truth for reactivation writes.
 * Creates a NEW Regimen document; never mutates existing docs.
 */
export const createReactivatedRegimen = (
  primaryId: string,
  usage: Usage,
  newStartDate: string,
  uid: string,
  listId: string,
): Promise<void> => {
  const newRecord: Regimen = {
    id: randomId(),
    startDate: newStartDate,
    primaryId,
    list: listId,
    usages: [usage],
    uid,
  }
  return setDoc(
    doc(getEffectiveServices().firestore, `regimens/${newRecord.id}`),
    newRecord,
  )
}

interface RegimenListItem {
  label: string
  fieldId: string
  items: { [itemId: string]: string }
  id: string
}

interface UseRegimenFormProps {
  lists: RegimenListItem[]
  editingId?: string
  editingUsageIndex?: number
  records: Regimen[]
  reportingForEnd: string
  amountEnabled?: boolean
  frequencyEnabled?: boolean
  durationEnabled?: boolean
  methodEnabled?: boolean
  percentageEnabled?: boolean
  onClose: () => void
  reactivatingPrimaryId?: string
  initialStartDate?: string
  initialFrequency?: string
  initialFrequencyUnit?: FrequencyUnit
  initialAmount?: string
  initialAmountUnit?: AmountUnit
  initialMethod?: Method
  initialSelectedFields?: SelectedFields
  initialDuration?: string
  initialDurationUnit?: DurationUnit
  initialPercentage?: Percentage
}

export default function useRegimenForm({
  lists,
  editingId,
  editingUsageIndex,
  records,
  reportingForEnd,
  amountEnabled,
  frequencyEnabled,
  durationEnabled,
  methodEnabled,
  percentageEnabled,
  onClose,
  reactivatingPrimaryId,
  initialStartDate,
  initialFrequency,
  initialFrequencyUnit,
  initialAmount,
  initialAmountUnit,
  initialMethod,
  initialSelectedFields,
  initialDuration,
  initialDurationUnit,
  initialPercentage,
}: UseRegimenFormProps) {
  const [selectedFields, setSelectedFields] = useState<SelectedFields>(
    initialSelectedFields || {},
  )
  const [startDate, setStartDate] = useState(initialStartDate || '')
  const [frequency, setFrequency] = useState(initialFrequency || '')
  const [amount, setAmount] = useState(initialAmount || '')
  const [duration, setDuration] = useState(initialDuration || '')
  const [method, setMethod] = useState<Method | undefined>(initialMethod)
  const [frequencyUnit, setFrequencyUnit] = useState<FrequencyUnit | undefined>(
    initialFrequencyUnit,
  )
  const [amountUnit, setAmountUnit] = useState<AmountUnit | undefined>(
    initialAmountUnit,
  )
  const [durationUnit, setDurationUnit] = useState<DurationUnit | undefined>(
    initialDurationUnit,
  )
  const [percentage, setPercentage] = useState<Percentage | undefined>(
    initialPercentage,
  )

  const toast = useToast()
  const uid = useActiveUid()

  // Update state when initial props change (e.g., when clicking different rows)
  useEffect(() => {
    if (editingId) {
      setSelectedFields(initialSelectedFields || {})
      setStartDate(initialStartDate || '')
      setFrequency(initialFrequency || '')
      setAmount(initialAmount || '')
      setDuration(initialDuration || '')
      setMethod(initialMethod)
      setFrequencyUnit(initialFrequencyUnit)
      setAmountUnit(initialAmountUnit)
      setDurationUnit(initialDurationUnit)
      setPercentage(initialPercentage)
    }
  }, [
    editingId,
    initialSelectedFields,
    initialStartDate,
    initialFrequency,
    initialAmount,
    initialDuration,
    initialMethod,
    initialFrequencyUnit,
    initialAmountUnit,
    initialDurationUnit,
    initialPercentage,
  ])

  // Sync state when a new reactivation is triggered (start date always blank)
  useEffect(() => {
    if (reactivatingPrimaryId) {
      setSelectedFields(initialSelectedFields || {})
      setStartDate('')
      setFrequency(initialFrequency || '')
      setAmount(initialAmount || '')
      setDuration(initialDuration || '')
      setMethod(initialMethod)
      setFrequencyUnit(initialFrequencyUnit)
      setAmountUnit(initialAmountUnit)
      setDurationUnit(initialDurationUnit)
      setPercentage(initialPercentage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactivatingPrimaryId])

  const clear = () => {
    setStartDate('')
    setSelectedFields({})
    setFrequency('')
    setFrequencyUnit(undefined)
    setAmount('')
    setDuration('')
    setMethod(undefined)
    setAmountUnit(undefined)
    setDurationUnit(undefined)
    setPercentage(undefined)
  }

  const handleClose = () => {
    clear()
    onClose()
  }

  const getBrandIfDuplicate = (regimenId: string) => {
    const brandList = lists.find((list) => list.label === 'Brand')
    let brand = ''

    records.forEach((record) => {
      if (brand) return
      if (
        record.usages.at(-1)?.fields?.supplementId === regimenId &&
        record.endDate === undefined
      ) {
        const brandId = record.usages.at(-1)?.fields?.brandId
        if (brandId) {
          brand = brandList?.items[brandId] ?? ''
        }
      }
    })
    return brand
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const editingRecord = editingId
      ? records.find((r) => r.id === editingId)
      : undefined

    if (!uid) {
      return toast({
        status: 'error',
        title: 'Error',
        description: 'Could not determine user ID.',
        isClosable: true,
      })
    }
    if (editingRecord && editingRecord.uid !== uid) {
      return toast({
        status: 'error',
        title: 'Ownership mismatch',
        description: 'This regimen does not belong to the effective patient.',
        isClosable: true,
      })
    }

    // Reactivation path — create a new regimen doc preserving the same primaryId
    if (reactivatingPrimaryId) {
      if (!startDate || !isRawDateValid(startDate)) {
        return toast({
          status: 'error',
          title: 'Error',
          description:
            'You have entered an invalid Start Date. Please enter in the format YYYY-MM-DD.',
          isClosable: true,
        })
      }
      if (startDate > reportingForEnd) {
        return toast({
          status: 'error',
          title: 'Error',
          description: 'Start date must be in or before the current month.',
        })
      }
      const reactivationUsage: {
        fields: SelectedFields
        startDate: string
        frequency?: number
        frequencyUnit?: FrequencyUnit
        amount?: number
        amountUnit?: AmountUnit
        method?: Method
      } = { fields: selectedFields, startDate }
      if (amountEnabled) {
        const amountFloat = parseFloat(amount)
        if (!amountUnit || isNaN(amountFloat) || amountFloat <= 0) {
          return toast({
            status: 'error',
            title: 'Error',
            description: 'Invalid Amount',
          })
        }
        reactivationUsage.amount = amountFloat
        reactivationUsage.amountUnit = amountUnit
      }
      if (frequencyEnabled) {
        const frequencyInt = parseInt(frequency)
        if (!frequencyUnit || isNaN(frequencyInt) || frequencyInt === 0) {
          return toast({
            status: 'error',
            title: 'Error',
            description: 'Invalid Frequency',
          })
        }
        reactivationUsage.frequency = frequencyInt
        reactivationUsage.frequencyUnit = frequencyUnit
      }
      if (methodEnabled) {
        if (!method) {
          return toast({
            status: 'error',
            title: 'Error',
            description: 'Invalid method',
          })
        }
        reactivationUsage.method = method
      }
      createReactivatedRegimen(
        reactivatingPrimaryId,
        reactivationUsage as Usage,
        startDate,
        uid,
        lists[0].id,
      )
      handleClose()
      return
    }

    const newRecord: Regimen = editingRecord
      ? { ...editingRecord }
      : {
          startDate,
          id: randomId(),
          primaryId: selectedFields[lists[0].fieldId],
          list: lists[0].id,
          usages: [],
          uid: uid,
        }

    if (startDate && !isRawDateValid(startDate)) {
      return toast({
        status: 'error',
        title: 'Error',
        description:
          'You have entered an invalid Start Date. Please enter in the format YYYY-MM-DD.',
        isClosable: true,
      })
    }

    if (startDate > reportingForEnd) {
      return toast({
        status: 'error',
        title: 'Error',
        description: 'Start date must be in or before the current month.',
      })
    }

    // Create usage with proper typing
    const usage: {
      fields: SelectedFields
      startDate: string
      frequency?: number
      frequencyUnit?: FrequencyUnit
      percentage?: Percentage
      amount?: number
      amountUnit?: AmountUnit
      duration?: number
      durationUnit?: DurationUnit
      method?: Method
    } = {
      fields: selectedFields,
      startDate,
    }

    if (frequencyEnabled) {
      const frequencyInt = parseInt(frequency)
      if (!frequencyUnit || isNaN(frequencyInt) || frequencyInt === 0) {
        return toast({
          status: 'error',
          title: 'Error',
          description: 'Invalid Frequency',
        })
      }
      usage.frequency = frequencyInt
      usage.frequencyUnit = frequencyUnit
      if (frequencyUnit === 'one_time') {
        const endDate = new Date(startDate + 'T00:00:00')
        const durationInt = parseInt(duration)
        if (durationUnit === 'days' && durationInt > 1) {
          endDate.setDate(endDate.getDate() + durationInt - 1)
        }
        newRecord.endDate = moment(endDate).format('YYYY-MM-DD')
      }
    }

    if (percentageEnabled) {
      if (!percentage) {
        return toast({
          status: 'error',
          title: 'Error',
          description: 'Invalid Percentage',
        })
      }
      usage.percentage = percentage
    }

    if (amountEnabled) {
      const amountFloat = parseFloat(amount)
      if (!amountUnit || isNaN(amountFloat) || amountFloat <= 0) {
        return toast({
          status: 'error',
          title: 'Error',
          description: 'Invalid Amount',
        })
      }
      usage.amount = amountFloat
      usage.amountUnit = amountUnit
    }

    if (durationEnabled) {
      const durationInt = parseInt(duration)
      if (!durationUnit || isNaN(durationInt) || durationInt === 0) {
        return toast({
          status: 'error',
          title: 'Error',
          description: 'Invalid Duration',
        })
      }
      usage.duration = durationInt
      usage.durationUnit = durationUnit
    }

    if (methodEnabled) {
      if (!method) {
        return toast({
          status: 'error',
          title: 'Error',
          description: 'Invalid method',
        })
      }
      usage.method = method
    }

    // Update the correct usage based on editingUsageIndex
    if (editingId && editingUsageIndex !== undefined) {
      // Update the specific usage at the given index
      // Note: usageIndex in the UI is reversed, so we need to convert it
      const actualIndex = newRecord.usages.length - 1 - editingUsageIndex
      newRecord.usages[actualIndex] = usage
    } else if (
      newRecord.usages.length > 0 &&
      newRecord.usages[newRecord.usages.length - 1].startDate ===
        usage.startDate
    ) {
      // Original logic: update last usage if startDate matches
      newRecord.usages[newRecord.usages.length - 1] = usage
    } else {
      // Add new usage
      newRecord.usages.push(usage)
    }

    // Save to Firestore
    setDoc(
      doc(getEffectiveServices().firestore, `regimens/${newRecord.id}`),
      newRecord,
    )
    handleClose()
  }

  const hookCreateReactivatedRegimen = (
    primaryId: string,
    usage: Usage,
    newStartDate: string,
  ) => {
    if (!uid) {
      return toast({
        status: 'error',
        title: 'Error',
        description: 'Could not determine user ID.',
        isClosable: true,
      })
    }
    createReactivatedRegimen(primaryId, usage, newStartDate, uid, lists[0].id)
  }

  return {
    selectedFields,
    setSelectedFields,
    startDate,
    setStartDate,
    frequency,
    setFrequency,
    amount,
    setAmount,
    duration,
    setDuration,
    method,
    setMethod,
    frequencyUnit,
    setFrequencyUnit,
    amountUnit,
    setAmountUnit,
    durationUnit,
    setDurationUnit,
    percentage,
    setPercentage,
    handleSubmit,
    handleClose,
    getBrandIfDuplicate,
    createReactivatedRegimen: hookCreateReactivatedRegimen,
  }
}
